import { RpcError } from "grpc-web";
import { GetUserResponse } from "./../proto/api_pb.d";
import { useCallback } from "react";
import { OmmuseClient } from "../proto/api_grpc_web_pb";
import { GetUserRequest } from "../proto/api_pb";
import { backOff } from "exponential-backoff";
import { useAuth0 } from "@auth0/auth0-react";
import useGetValidAccessToken from "./useGetValidAccessToken";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

type ReturnType = {
  isUserLoading: boolean;
  getUserResponse: GetUserResponse | undefined;
  getUserError: RpcError | undefined;
  emailVerified: boolean | undefined;
  refreshUserDetails: () => void;
};

const fetchUser = async (
  userId: string,
  accessToken: string
): Promise<GetUserResponse> => {
  const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const request = new GetUserRequest();
  request.setUserId(userId);
  const metadata = {
    Authorization: `${accessToken}`,
    "Access-Control-Allow-Origin": "*",
  };
  return backOff(
    () =>
      new Promise<GetUserResponse>((resolve, reject) => {
        ommuseClient.getUser(request, metadata, (err, response) => {
          if (err) reject(err);
          else resolve(response);
        });
      }),
    { numOfAttempts: 2, jitter: "full", timeMultiple: 5 }
  );
};

export const useGetUser = (
  userId?: string,
  _profileUpdated?: boolean,
  shareToken?: string
): ReturnType => {
  const { user } = useAuth0();
  const { getValidAccessToken } = useGetValidAccessToken();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<GetUserResponse, RpcError>({
    queryKey: ["user", userId, shareToken],
    queryFn: async () => {
      const accessToken = shareToken
        ? shareToken
        : await getValidAccessToken();
      return fetchUser(userId!, accessToken);
    },
    enabled: !!userId && userId !== "",
    onError: (err: RpcError) => {
      Sentry.captureException(err);
    },
  } as any);

  const refreshUserDetails = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["user", userId] });
  }, [queryClient, userId]);

  return {
    isUserLoading: isLoading,
    getUserResponse: data,
    getUserError: error ?? undefined,
    emailVerified: user ? user.email_verified : undefined,
    refreshUserDetails,
  };
};
